import { useEffect, useState } from "react";
import { useSearchParams, Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";

export function BillingPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(null);
  const [seats, setSeats] = useState(null);
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);

  const checkPromo = async () => {
    if (!promo.trim()) { setPromoInfo(null); return; }
    try {
      const { data } = await api.get(`/billing/promo/${encodeURIComponent(promo.trim())}`);
      setPromoInfo({ ok: true, ...data });
    } catch { setPromoInfo({ ok: false }); }
  };

  const loadAll = () => {
    api.get("/billing/status").then((r) => setStatus(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/billing/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const cancelSub = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription cancelled — you're now on the Starter feature set");
      loadAll();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };

  const subscribe = async (code) => {
    setBusy(code);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: code, seats: effSeats, origin_url: window.location.origin,
        promo_code: promoInfo?.ok ? promoInfo.code : undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };

  const effSeats = seats ?? status?.subscription?.seats ?? status?.usage?.users ?? 3;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card px-6 py-3 flex justify-between items-center">
        <Link to="/projects" className="font-display font-black tracking-tighter text-lg">Ganttastic.app</Link>
        <span className="text-sm text-muted-foreground">{user?.tenantName}</span>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8" data-testid="billing-page">
        <PageHeader overline="Subscription & Billing" title="Plans" />
        {status && (
          <div data-testid="billing-status" className="border border-border bg-card p-4 mb-6 flex flex-wrap gap-6 items-center">
            <div><div className="overline-label">Current plan</div><div className="font-display font-black text-xl">{status.plan.name}</div></div>
            <StatusBadge value={status.subscription.status} />
            {status.subscription.trialEnd && status.subscription.status === "Trialing" && (
              <span className="text-xs text-muted-foreground">Trial ends {status.subscription.trialEnd.slice(0, 10)}</span>)}
            <div className="text-xs text-muted-foreground">Usage: {status.usage.projects} projects · {status.usage.users} users
              (limits: {status.plan.maxProjects} projects / {status.plan.seatLimit ?? status.plan.maxUsers} seats)</div>
            <div className="flex gap-2 text-xs">
              {[["AI", status.plan.aiEnabled], ["Six Sigma", status.plan.sixSigmaEnabled], ["Lean", status.plan.leanEnabled]].map(([l, v]) => (
                <StatusBadge key={l} value={`${l}: ${v ? "ON" : "OFF"}`} tone={v ? "green" : "neutral"} />))}
            </div>
            {status.subscription.status === "Active" && (
              <Button data-testid="cancel-subscription-btn" variant="outline" onClick={cancelSub} disabled={busy === "cancel"}
                className="rounded-none border-red-400 text-red-600 hover:bg-red-50 ml-auto">
                {busy === "cancel" ? "Cancelling…" : "Cancel subscription"}
              </Button>
            )}
          </div>
        )}
        <div data-testid="billing-seats-block" className="border border-border bg-card p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <Label>Seats — how many people need access?</Label>
            <span data-testid="billing-seats-value" className="font-display font-black text-xl">{effSeats} users</span>
          </div>
          <Slider data-testid="billing-seats-slider" value={[effSeats]} min={1} max={100} step={1} onValueChange={(v) => setSeats(v[0])} />
          <p className="text-xs text-muted-foreground mt-2">
            You're billed per seat when you subscribe or switch plans below. Each plan has a minimum: Starter 3 · Professional 5 · Enterprise 10 — we'll apply it automatically.
            {status?.subscription?.seats ? ` Current subscription: ${status.subscription.seats} seats.` : ""}
          </p>
          <div className="flex items-center gap-2 mt-3 max-w-sm">
            <Input data-testid="billing-promo-input" className="h-9" placeholder="Promo code (optional)"
              value={promo} onChange={(e) => { setPromo(e.target.value); setPromoInfo(null); }} onBlur={checkPromo} />
            <Button data-testid="billing-promo-apply-btn" variant="outline" className="rounded-none h-9" onClick={checkPromo} disabled={!promo.trim()}>Apply</Button>
          </div>
          {promoInfo?.ok && (
            <div data-testid="billing-promo-valid" className="text-xs text-emerald-600 mt-1.5 font-semibold">
              ✓ {promoInfo.code}: {promoInfo.percentOff ? `${promoInfo.percentOff}% off` : `$${promoInfo.amountOff} off`}
              {promoInfo.duration === "repeating" ? ` for ${promoInfo.durationInMonths} months` : promoInfo.duration === "once" ? " (first payment)" : ""} — applied at checkout
            </div>
          )}
          {promoInfo && !promoInfo.ok && <div data-testid="billing-promo-invalid" className="text-xs text-red-600 mt-1.5">Invalid or expired promo code</div>}
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {plans.filter((p) => p.interval !== "year").map((p) => (
            <div key={p.code} data-testid={`plan-card-${p.code}`} className={`border bg-card p-5 ${status?.subscription?.planCode === p.code ? "border-[#1E6FE8]" : "border-border"}`}>
              <div className="overline-label">{p.name}</div>
              <div className="font-display font-black text-3xl tracking-tighter mt-1">${p.price}<span className="text-sm font-normal text-muted-foreground">/user/mo</span></div>
              <div className="text-xs text-muted-foreground">≈ ${(p.price * Math.min(Math.max(effSeats, p.minSeats || 1), p.maxUsers)).toLocaleString()}/mo for {Math.min(Math.max(effSeats, p.minSeats || 1), p.maxUsers)} seats</div>
              <ul className="text-xs space-y-1 mt-3 text-muted-foreground">
                <li>{p.maxProjects >= 9999 ? "Unlimited" : p.maxProjects} projects · min {p.minSeats} seats · up to {p.maxUsers >= 9999 ? "unlimited" : p.maxUsers} users</li>
                <li>AI assistant: {p.aiEnabled ? "included" : "—"}</li>
                <li>Six Sigma toolkit: {p.sixSigmaEnabled ? "included" : "—"}</li>
                <li>Lean toolkit: {p.leanEnabled ? "included" : "—"}</li>
              </ul>
              <Button data-testid={`subscribe-${p.code}-btn`} className="rounded-none w-full mt-4 bg-[#1E6FE8] hover:bg-[#1557C6]"
                onClick={() => subscribe(p.code)} disabled={busy === p.code}>
                {busy === p.code ? "Redirecting…"
                  : status?.subscription?.planCode === p.code && status?.subscription?.status === "Active" ? "Update seats"
                  : status?.subscription?.status === "Active" ? "Switch to this plan" : "Subscribe"}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-8" data-testid="invoices-section">
          <div className="overline-label mb-2">Invoices</div>
          <div className="border border-border bg-card overflow-auto">
            <table className="w-full data-table text-sm">
              <thead><tr><th>Date</th><th>Invoice #</th><th>Amount</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {invoices.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">No invoices yet — they appear after your first payment.</td></tr>}
                {invoices.map((i) => (
                  <tr key={i.id} data-testid="invoice-row">
                    <td>{i.created}</td>
                    <td className="font-mono2 text-xs">{i.number || i.id}</td>
                    <td>${i.amountPaid.toFixed(2)} {i.currency?.toUpperCase()}</td>
                    <td><StatusBadge value={i.status} /></td>
                    <td>{i.hostedUrl && <a className="underline text-[#1E6FE8] text-xs" href={i.hostedUrl} target="_blank" rel="noreferrer">View / PDF</a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4">Test card: 4242 4242 4242 4242 · any future expiry · any CVC</p>
      </main>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {}
      if (++tries < 10) setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div data-testid="payment-result" className="border border-border bg-card p-8 max-w-md text-center">
        {state === "checking" && <p>Confirming your payment…</p>}
        {state === "paid" && <>
          <h2 className="font-display font-black text-2xl tracking-tighter text-[#00a86b]">Subscription active</h2>
          <p className="text-sm text-muted-foreground mt-2">Your plan has been upgraded. All plan features are now unlocked.</p>
        </>}
        {["failed", "timeout", "error"].includes(state) && <>
          <h2 className="font-display font-black text-2xl tracking-tighter text-red-600">Payment not confirmed</h2>
          <p className="text-sm text-muted-foreground mt-2">Check your email or retry from the Billing page.</p>
        </>}
        <Link to="/billing" className="inline-block mt-4 underline text-[#1E6FE8] text-sm">Back to Billing</Link>
      </div>
    </div>
  );
}

export function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`).then((r) => setInvite(r.data)).catch((e) => setErr(errMsg(e)));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`, form);
      login(data);
      navigate(`/projects/${data.projectId}/dashboard`);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div data-testid="invite-accept-page" className="border border-border bg-card p-8 max-w-md w-full space-y-3">
        <div className="overline-label">Project invitation</div>
        {err && <p className="text-sm text-red-600">{err}</p>}
        {invite && <>
          <h2 className="font-display font-black text-2xl tracking-tighter">{invite.projectName}</h2>
          <p className="text-sm text-muted-foreground">You've been invited as <StatusBadge value={invite.role} tone="blue" /> ({invite.email})</p>
          <div><Label>Your name</Label><Input data-testid="invite-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Choose a password</Label><Input data-testid="invite-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <Button data-testid="accept-invite-btn" className="rounded-none w-full bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={accept} disabled={busy || !form.name || !form.password}>
            {busy ? "Joining…" : "Accept & join project"}
          </Button>
        </>}
      </div>
    </div>
  );
}
